import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgOtpInputModule } from 'ng-otp-input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService, UserService } from '@app/shared/services';
import { Subscription, interval } from 'rxjs';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
@AutoUnsubscribe()
@Component({
  selector: 'app-email-multifactor-authentication',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatCheckboxModule, NgOtpInputModule, MatFormFieldModule, MatInputModule, MatButtonModule],
  templateUrl: './email-multifactor-authentication.component.html',
  styleUrl: './email-multifactor-authentication.component.scss',
})
export class EmailMultifactorAuthenticationComponent implements OnInit, OnDestroy {
  form: FormGroup;
  step = 1;
  otp: string = '';
  counter = 1800; // 5 minutes in seconds
  errorMsg: string = '';
  verificationCompleted = false;
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    public dialogRef: MatDialogRef<EmailMultifactorAuthenticationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { accountEmail: string },
    private _userService: UserService
  ) {
    this.form = this.fb.group({
      useAccountEmail: [true],
      email: [{ value: this.data.accountEmail, disabled: true }, [Validators.required, Validators.email]],
    });

    const useAccountEmailControl = this.form.get('useAccountEmail');
    if (useAccountEmailControl) {
      this.subscription.add(
        useAccountEmailControl.valueChanges.subscribe((useAccount) => {
          const emailControl = this.form.get('email');
          if (useAccount) {
            emailControl?.disable();
            emailControl?.setValue(this.data.accountEmail);
          } else {
            emailControl?.enable();
          }
        })
      );
    }
  }

  ngOnInit() {
    this.startTimer();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  startTimer() {
    this.subscription.add(
      interval(1000).subscribe(() => {
        if (this.counter > 0) {
          this.counter--;
        } else {
          this.errorMsg = 'Code has expired. Please request a new one.';
        }
      })
    );
  }

  transform(value: number): string {
    const minutes: number = Math.floor(value / 60);
    return ('00' + minutes).slice(-2) + ':' + ('00' + Math.floor(value - minutes * 60)).slice(-2);
  }

  onOtpChange(event) {
    this.otp = event;
  }

  async sendOtp() {
    if (this.form.valid) {
      try {
        await this.authService.sendUserEmailVerification();
        this.step = 2;
        this.counter = 150;
        this.startTimer();
        this.errorMsg = '';
      } catch (error) {
        this.errorMsg = 'Failed to send verification code. Please try again.';
      }
    }
  }

  saveData(value) {
    const dataObj = {
      mfa_enabled: value,
    };
    this.subscription.add(this._userService.updateUser(dataObj).subscribe(() => {}));
  }

  async verifyOtp() {
    if (this.otp.length === 6) {
      try {
        const res = await this.authService.confirmUserEmail(this.otp);
        if (res.success) {
          // Enable email MFA
          this.verificationCompleted = true;
          this.step = 3;
          this.errorMsg = '';
          this.saveData(true);
          await this.authService.enableCurrentUserMFA('email');
        } else {
          this.errorMsg = 'Incorrect code. Please re-enter.';
        }
      } catch (error) {
        this.errorMsg = 'Incorrect code. Please re-enter.';
      }
    }
  }

  async resendOtp() {
    if (this.form.valid) {
      try {
        await this.authService.sendUserEmailVerification();
        this.counter = 300;
        this.startTimer();
        this.errorMsg = '';
      } catch (error) {
        this.errorMsg = 'Failed to resend verification code. Please try again.';
      }
    }
  }

  close() {
    this.dialogRef.close(this.verificationCompleted);
  }
}
